import React, { DependencyList, useCallback, useState } from 'react'
import { Typography } from '@equinor/eds-core-react'
import { useModal } from 'react-modal-hook'
import ConfirmDialog from '@/components/confirmDialog'

export interface DownlinkConfirmDialogOptions<TData> {
    title?: string
    content1?: React.ReactNode | ((data?: TData) => React.ReactNode)
    content2?: React.ReactNode | ((data?: TData) => React.ReactNode)
    onConfirm?: (data?: TData) => void
    onReject?: (data?: TData) => void
    confirmButtonTitle?: string
    rejectButtonTitle?: string
    hideRejectButton?: boolean
    autoClose?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useDownlinkConfirmDialog = <TData extends unknown>(
    options: DownlinkConfirmDialogOptions<TData>,
    inputs?: DependencyList
) => {
    const [data, setData] = useState<TData>()

    const [showModal, hideModal] = useModal(() => {
        const content1 = typeof options.content1 === 'function' ? options.content1(data) : options.content1
        const content2 = typeof options.content2 === 'function' ? options.content2(data) : options.content2

        return (
            <ConfirmDialog
                title={options.title}
                onConfirmClick={onConfirmClick}
                onRejectClick={onRejectClick}
                onClose={onCloseClick}
                confirmButtonTitle={options.confirmButtonTitle}
                rejectButtonTitle={options.rejectButtonTitle}
                hideRejectButton={options.hideRejectButton}
                width="90vw"
                maxWidth="600px"
            >
                <Typography variant="body_short" component="div">
                    {content1 && <div style={{ whiteSpace: 'pre-line' }}>{content1}</div>}
                    {content2 && <div style={{ whiteSpace: 'pre-line', marginTop: '16px' }}>{content2}</div>}
                </Typography>
            </ConfirmDialog>
        )
    }, [data, inputs])

    const onConfirmClick = () => {
        options.onConfirm?.(data)
        if (options.autoClose === true || options.autoClose === undefined) hideModal()
    }

    const onRejectClick = () => {
        options.onReject?.(data)
        if (options.autoClose === true || options.autoClose === undefined) hideModal()
    }

    const onCloseClick = () => {
        if (options.autoClose === true || options.autoClose === undefined) hideModal()
    }

    const showConfirm = useCallback(
        (data?: TData) => {
            setData(data)
            showModal()
        },
        [showModal]
    )

    const hideConfirm = useCallback(() => hideModal(), [hideModal])

    return { showConfirm, hideConfirm }
}
